import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";

export default function Shop() {
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const activeBrand = params.get("brand") || "all";
  const activeCat = params.get("cat") || "all";
  const activeSub = params.get("sub") || "";

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const p = {};
    if (activeBrand !== "all") p.brand = activeBrand;
    if (activeSub) p.sub = activeSub;
    else if (activeCat !== "all") p.cat = activeCat;
    api.get("/products", { params: p }).then((r) => setProducts(r.data)).catch(() => {});
  }, [activeBrand, activeCat, activeSub]);

  const setFilter = (key, value) => {
    const next = {};
    const brand = key === "brand" ? value : activeBrand;
    const cat = key === "cat" ? value : activeCat;
    const sub = key === "sub" ? value : key === "cat" ? "" : activeSub;
    if (brand !== "all") next.brand = brand;
    if (cat !== "all") next.cat = cat;
    if (sub) next.sub = sub;
    setParams(next);
  };

  const activeCatObj = categories.find((c) => c.slug === activeCat);

  const filtered = useMemo(
    () => products.filter((p) => p.name.toLowerCase().includes(query.toLowerCase())),
    [products, query]
  );

  const brandName = (slug) => brands.find((b) => b.slug === slug)?.name || slug;

  return (
    <main data-testid="shop-page">
      <Navbar />
      <section className="pt-40 pb-24 border-b border-zinc-200 relative overflow-hidden">
        <div className="spotlight absolute inset-0" aria-hidden="true" />
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-xs uppercase tracking-[0.5em] text-gold mb-6">Skincare · Haircare · Grooming · Wellness</p>
            <h1 className="font-serif font-medium text-5xl sm:text-6xl lg:text-8xl text-zinc-900 tracking-tight" data-testid="shop-heading">
              The <em className="text-gold">Collection</em>
            </h1>
            <p className="text-zinc-500 mt-6 max-w-xl">
              {filtered.length} formulas across {brands.length} brands.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col gap-6 mb-14">
            <div className="flex flex-wrap gap-3" data-testid="brand-filters">
              {[{ slug: "all", name: "All Brands" }, ...brands].map((b) => (
                <button
                  key={b.slug}
                  onClick={() => setFilter("brand", b.slug)}
                  data-testid={`filter-${b.slug}`}
                  className={`text-[10px] uppercase tracking-[0.3em] px-5 py-2.5 border transition-colors duration-300 ${
                    activeBrand === b.slug
                      ? "bg-gold text-white border-gold"
                      : "border-zinc-200 text-zinc-500 hover:border-gold/50 hover:text-gold"
                  }`}
                >
                  {b.name}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3" data-testid="category-filters">
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mr-2">Categories</span>
              {[{ slug: "all", name: "All" }, ...categories].map((c) => (
                <button
                  key={c.slug}
                  onClick={() => setFilter("cat", c.slug)}
                  data-testid={`cat-filter-${c.slug}`}
                  className={`text-[10px] uppercase tracking-[0.3em] px-5 py-2.5 border rounded-full transition-colors duration-300 ${
                    activeCat === c.slug
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "border-zinc-200 text-zinc-500 hover:border-gold/50 hover:text-gold"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            {activeCatObj && (activeCatObj.subs || []).length > 0 && (
              <div className="flex flex-wrap items-center gap-2" data-testid="subcategory-filters">
                <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mr-2">In {activeCatObj.name}</span>
                <button
                  onClick={() => setFilter("sub", "")}
                  data-testid="sub-filter-all"
                  className={`text-[10px] uppercase tracking-[0.25em] px-4 py-2 border rounded-full transition-colors duration-300 ${
                    !activeSub ? "bg-gold text-white border-gold" : "border-zinc-200 text-zinc-500 hover:border-gold/50 hover:text-gold"
                  }`}
                >
                  All
                </button>
                {activeCatObj.subs.map((s) => (
                  <button
                    key={s.slug}
                    onClick={() => setFilter("sub", activeSub === s.slug ? "" : s.slug)}
                    data-testid={`sub-filter-${s.slug}`}
                    className={`text-[10px] uppercase tracking-[0.25em] px-4 py-2 border rounded-full transition-colors duration-300 ${
                      activeSub === s.slug ? "bg-gold text-white border-gold" : "border-zinc-200 text-zinc-500 hover:border-gold/50 hover:text-gold"
                    }`}
                  >
                    {s.name} ({s.count})
                  </button>
                ))}
              </div>
            )}
            <div className="relative lg:w-96">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the collection"
                data-testid="shop-search-input"
                className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm pl-11 pr-4 py-3 placeholder:text-zinc-400 focus:outline-none focus:border-gold/60 transition-colors duration-300"
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="text-zinc-400 font-serif italic text-2xl py-24" data-testid="shop-empty">
              Nothing matches — yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10" data-testid="shop-grid">
              {filtered.map((p, i) => (
                <ProductCard key={p.id} product={p} brandName={brandName(p.brand_slug)} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer brands={brands} />
    </main>
  );
}
